import { useState } from "react";
import { PhoneCall, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { TELEPHONY_CALL_URL } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

/**
 * Click-to-call through the tenant's telephony provider (Twilio / Smartflo).
 *
 * The provider rings the staff member's own phone first, then bridges the
 * customer in — so the toast tells them to pick up, not to wait.
 *
 * Props:
 *   enquiryId / customerId — what the call is logged against (one of them)
 *   number                 — optional; overrides the record's own number
 *   size                   — 'sm' (default) | 'xs' for inline next to a phone number
 *   onCalled               — fired after the provider accepted the call
 */
export default function CallButton({ enquiryId, customerId, number, size = "sm", onCalled, className }) {
    const { create } = useApiMethods();
    const [pending, setPending] = useState(false);

    const call = () => {
        if (pending) return;
        const body = enquiryId ? { enquiry_id: enquiryId } : { customer_id: customerId };
        if (number) body.number = number;
        setPending(true);
        create(TELEPHONY_CALL_URL, body)
            .then((res) => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message || "Calling you now — pick up to be connected.");
                    onCalled?.(res.data);
                } else {
                    ToastMsg("error", res?.message || "Could not start the call");
                }
            })
            .catch((err) => ToastMsg("error", err?.response?.data?.message || "Could not start the call"))
            .finally(() => setPending(false));
    };

    if (size === "xs") {
        return (
            <button type="button" onClick={call} disabled={pending || (!enquiryId && !customerId)}
                title="Call through your phone"
                className={cn("p-1 rounded text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50", className)}>
                {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PhoneCall className="h-3.5 w-3.5" />}
            </button>
        );
    }

    return (
        <Button size="sm" variant="outline" onClick={call}
            disabled={pending || (!enquiryId && !customerId)}
            title="Rings your phone first, then connects the customer"
            className={className}>
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <PhoneCall className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />}
            {pending ? "Calling…" : "Call"}
        </Button>
    );
}
